import React, { useState } from "react";
import { useNavigate } from "react-router";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import useMediaQuery from "@mui/material/useMediaQuery";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import SaveIcon from "@mui/icons-material/SaveOutlined";
import AddIcon from "@mui/icons-material/Add";
import { useAssetsCollection } from "../../contexts/assetsCollection";
import useQueryParams from "../../hooks/useQueryParams";
import SelectProjectAttentionModal from "./SelectProjectAttentionModal";
import { useProject } from "../../contexts/project";
import useAuth from "../../hooks/useAuth";

export default function CaptureActionsMobile({
  disabled,
  onSave,
  onCreate,
  onDuplicate,
  projectId,
  disableSave,
  canWrite = true,
}) {
  const [isAttentionOpen, setIsAttentionOpen] = useState(false);
  const params = useQueryParams();
  const { handeRefetch } = useAssetsCollection();
  const navigate = useNavigate();
  const currentProject = useProject();
  const { user } = useAuth();
  const { settings } = user?.company || {};
  const showIcons = useMediaQuery('(min-width:500px)');

  const handleReceivingToggle = () => {
    if (params.get("receiving")) {
      handeRefetch();
      const newParams = new URLSearchParams(params);
      newParams.delete("receiving");
      newParams.delete("step");
      newParams.delete("editShipment");
      newParams.delete("shipmentId");
      newParams.delete("id");
      navigate({ search: newParams.toString() });
    } else {
      const newParams = new URLSearchParams(params);
      newParams.delete("isFormModalOpen");
      newParams.set("step", "details");
      newParams.set("receiving", "true");
      navigate({ search: newParams.toString() });
    }
  };

  const isReceivingWizardDisabled = params.get("disable_receiving_wizard") === "true";

  const handleCreate = () => {
    if (!projectId) {
      setIsAttentionOpen(true);
      return;
    }

    if (currentProject?.storageEnabled && !isReceivingWizardDisabled) {
      handleReceivingToggle();
    } else {
      onCreate();
    }
  };

  const isMobile = useMediaQuery(theme => theme.breakpoints.down("md"));

  const handleSave = async () => {
    if (onSave) {
      const result = await onSave();

      // Close modal on mobile after successful save
      if (isMobile && params.get("isFormModalOpen") === "true") {
        const newParams = new URLSearchParams(params);
        newParams.delete("isFormModalOpen");
        navigate({ search: newParams.toString() });
      }

      return result;
    }
  };

  const showDuplicate = onDuplicate && canWrite && (
    projectId 
      ? !currentProject?.storageEnabled || isReceivingWizardDisabled
      : !settings.receiving_wizard
  );
  const showSave = onSave && canWrite;
  const showCreate = onCreate && canWrite;

  return (
    <Card sx={{ p: 1.5, borderBottomLeftRadius: "0px", borderBottomRightRadius: "0px" }}>
      <Box display="flex" justifyContent="flex-end" gap={1}>
        {showDuplicate && (
          <Box flex="1 1 0" maxWidth="250px">
            <Button
              disabled={disabled || !onDuplicate || disableSave}
              id="duplicateAsset"
              data-id="duplicate-asset"
              variant="outlined"
              onClick={onDuplicate}
              fullWidth
              startIcon={showIcons ? <FileCopyIcon /> : null}
            >
              Duplicate
            </Button>
          </Box>
        )}
        {showSave && (
          <Box flex="1 1 0" maxWidth="250px">
            <Button
              disabled={disabled || !onSave || disableSave}
              id="saveAsset"
              data-id="save-asset"
              variant="outlined"
              onClick={handleSave}
              fullWidth
              startIcon={showIcons ? <SaveIcon /> : null}
            >
              Save
            </Button>
          </Box>
        )}
        {showCreate && (
          <Box flex="1 1 0" maxWidth="250px">
            <Button
              disabled={disabled || !onCreate}
              id="addNewAsset"
              data-id="add-new-asset"
              variant="contained"
              onClick={handleCreate}
              color="primary"
              fullWidth
              startIcon={showIcons ? <AddIcon /> : null}
            >
              Add New
            </Button>
          </Box>
        )}
      </Box>
      <SelectProjectAttentionModal
        open={isAttentionOpen}
        onClose={() => setIsAttentionOpen(false)}
      />
    </Card>
  );
}
